import { createAsyncThunk } from '@reduxjs/toolkit';
import { baseApi } from 'shared/api/base-api';
import { buildTournamentApplicationUserDto } from 'shared/utils/build-tournament-application-user-dto';
import { ChangeParticipantsRoleDto } from 'modules/teams/domain/dtos/change-participants-role';
import { KickFromTeamInputDto } from 'modules/teams/domain/dtos/kick-from-team-input.dto';
import { UpsertTournamentPlayerApplicationDto } from 'modules/teams/domain/dtos/upsert-tournament-player-action.dto';
import { Player } from 'modules/players/domain/interfaces/player';
import { UpsertTournamentApplicationUserDto } from 'modules/players/domain/interfaces/upsert-player-input.dto';
import playersRepository from 'modules/players/domain/repositories/player.repository';
import playersResource from 'modules/players/domain/resources/players-resource';
import teamParticipantsResource from 'modules/teams/domain/resources/team-participants-resource';

const PREFIX = 'teams/players';

/**
 * Мутации в проекте остаются thunk'ами, поэтому кэш RTK Query они сбрасывают
 * сами — иначе карточка команды покажет состав до изменения.
 */
const invalidateTeam = (teamId: number) => baseApi.util.invalidateTags([{ type: 'Team' as const, id: teamId }]);

export const upsertTournamentPlayerApplication = createAsyncThunk<Player, UpsertTournamentPlayerApplicationDto>(
  `${PREFIX}/upsertTournamentPlayerApplication`,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { tournamentId, userId, photo, teamId } = payload;
      let player: UpsertTournamentApplicationUserDto = buildTournamentApplicationUserDto(payload);

      let playerId: number | null = null;

      try {
        let result = await playersRepository.upsertTournamentApplicationForUser(player, tournamentId);
        playerId = result.id;
      } catch (err) {
        // Правка существующего игрока: он уже заведён в турнире, отказ мутации не мешает
        // добавить его в команду — продолжаем с переданным userId.
        if (!userId) {
          // Создание нового игрока: продолжать не с чем. Пробрасываем исходную ошибку —
          // на её тексте держатся тосты «Игрок уже в этой команде» и «Такой игрок уже
          // существует» в `create-player.tsx`. Раньше здесь терялся текст и вместо них
          // всегда показывалось общее «Не удалось выполнить операцию».
          throw err;
        }

        console.warn(`${PREFIX}/upsertTournamentPlayerApplication warning:`, err);
        playerId = userId;
      }

      // `instanceof File` — форма может отдать уже сохранённое фото строкой-ссылкой,
      // грузить его повторно не нужно. В modules/players проверка была, здесь — нет.
      if (photo?.[0] instanceof File) {
        await playersResource.uploadPhoto(photo[0], playerId, tournamentId);
      }

      if (!userId) {
        await playersRepository.addTournamentUserToTeam({
          userId: playerId,
          teamId,
        });
      }

      const user = await playersRepository.getUser(playerId);

      dispatch(invalidateTeam(teamId));

      return user;
    } catch (error) {
      console.error(`${PREFIX}/upsertTournamentPlayerApplication error:`, error);
      let message = error instanceof Error ? error.message : String(error);
      return rejectWithValue(message);
    }
  },
);

export const deleteTournamentPlayerApplication = createAsyncThunk<KickFromTeamInputDto, KickFromTeamInputDto>(
  `${PREFIX}/deleteTournamentPlayerApplication`,
  async (payload, { dispatch }) => {
    try {
      const { userId, teamId } = payload;

      await playersRepository.kickTournamentUserFromTeam({ teamId, userId });
      dispatch(invalidateTeam(teamId));

      return payload;
    } catch (err) {
      console.error(`${PREFIX}/deleteTournamentPlayerApplication error:`, err);
      throw err;
    }
  },
);

export const changePlayerRole = createAsyncThunk<string, ChangeParticipantsRoleDto>(
  `${PREFIX}/changePlayerRole`,
  async (payload, { dispatch }) => {
    try {
      const result = await teamParticipantsResource.changeRole(
        payload.teamId,
        payload.userId,
        payload.role,
        payload.number,
        payload.position,
      );
      dispatch(invalidateTeam(payload.teamId));

      return result;
    } catch (err) {
      console.error(`${PREFIX}/changePlayerRole error:`, err);
      throw err;
    }
  },
);
